import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkDrag, CdkDragHandle, CdkDropList, CdkDropListGroup, moveItemInArray } from '@angular/cdk/drag-drop';
import type {
    MiniCartSettingsFormType,
    ShopCartWidgetData,
    ShopCartWidgetDataFormType,
} from '@web/widgets/shop-cart/type/shop-cart-widget.type';
import type { CrmPipeline, CrmPipelineSetting, CrmPipelineStep } from '@integrations/crm-pipelines';
import { injectCrmPipelinesQuery } from '@integrations/crm-pipelines';
import type { MiniCartSettings } from '@common/types/mini-cart-settings.type';
import type { FormElementFormGroup, OptionFormArray } from '@common/types/form-element-forms.type';
import type { FormButtonSettingsFormType } from '@components/sp-form-button-settings';
import type { FormInputSettingsFormType } from '@components/sp-form-input-settings';
import type { CrmDeal } from '@integrations/crm-deals';
import { injectCrmDealsQuery } from '@integrations/crm-deals';
import { type CrmContact, injectCrmContactsQuery, isCrmDisabledError } from '@integrations/crm-contacts';
import type { AddressBookVariable } from '@integrations/address-books';
import { AddressBooksUiComponent, injectAddressBooksQuery } from '@integrations/address-books';
import type { WidgetErrors } from '@common/types/widget-errors.type';
import type { FormElement } from '@common/types/form-element.type';

import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, filter, map, startWith, take, takeUntil } from 'rxjs/operators';

import {
    EButtonType,
    ECartViewType,
    EFormInputType,
    type EHorizontalPosition,
    EHtmlAutocompleteAttr,
    EHtmlInputType,
    FormStyleEnum,
} from '@common/enums';
import { EPaymentType } from '@common/enums/payment.enum';
import { ERadioGroupType } from '@ui/sp-radio-group';

import type { PaymentElement, PaymentMethodOption } from '@integrations/payment-methods';
import { injectPaymentMethodsOptionsQuery, injectPaymentMethodsQuery } from '@integrations/payment-methods';

import { shopCartWidgetDataFormGroup } from '../../form/shop-cart-widget.form';

import {
    formElementForm,
    formElementOptionFormGroup,
    paymentElementFormGroup,
} from '@web/widgets/common/base/base-form/base-form.form';

import { WebBaseControlComponent } from '@web/widgets/common/abstract/web-base-control.component';
import { EPixelTypes } from '@common/types/analytics-setings.type';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import type { PaymentElementFormGroup, PaymentElementFormType } from '@common/types/payment-element.form';
import { HtmlToTextPipe } from '@common/pipes/html-to-text.pipe';
import { TranslateModule } from '@ngx-translate/core';
import { WebAnalyticsSettingsModule } from '@web/shared/components/web-analytics-settings/web-analytics-settings.module';
import { ShopCartIconSettingsComponent } from './shop-cart-icon-settings/shop-cart-icon-settings.component';
import { ShopCartMiniCartSettingsComponent } from './shop-cart-mini-cart-settings/shop-cart-mini-cart-settings.component';
import { ShopCartStyleSettingsComponent } from './shop-cart-style-settings/shop-cart-style-settings.component';
import { ShopCartFormPaymentMethodSettingsComponent } from './shop-cart-form-payment-method-settings/shop-cart-form-payment-method-settings.component';
import { ShopCartFormAdditionalSettingsComponent } from './shop-cart-form-additional-settings/shop-cart-form-additional-settings.component';
import { ShopCartFormInputSettingsComponent } from './shop-cart-form-input-settings/shop-cart-form-input-settings.component';
import { ShopCartFormElementSettingsComponent } from './shop-cart-form-element-settings/shop-cart-form-element-settings.component';
import { ShopCartFormButtonSettingsComponent } from './shop-cart-form-button-settings/shop-cart-form-button-settings.component';

import { SpControlHandleComponent } from '@ui/sp-control-handle';
import { SpSwitchComponent } from '@ui/sp-switch';
import { WebCrmPipelinesCollectionComponent } from '@web/shared/components/web-crm-pipelines-collection/web-crm-pipelines-collection.component';
import { NgxIntlTelInputModule } from '@moddi3/ngx-intl-tel-input';

import { WebSettingsModule } from '@web/shared/components/web-settings/web-settings.module';

import { SpVariablesPopoverComponent } from '@ui/sp-variables-popover';

import { WebCustomIdComponent } from '@web/shared/components/web-custom-id/web-custom-id.component';
import { WebPaymentMethodsCollectionComponent } from '@web/shared/components/web-payment-methods-collection/web-payment-methods-collection.component';

import type { CrmTag } from '@integrations/crm-tags';
import { injectCrmTagsQuery } from '@integrations/crm-tags';
import { WebTagsCollectionComponent } from '@web/shared/components/web-tags-collection/web-tags-collection.component';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { SiteFacade } from '@web/store/website/site/site.facade';
import { ShopCartIconHttpService } from '@common/services/REST/shop-cart-icon.http.service';
import { ShopCartPreviewService } from '@web/widgets/shop-cart/services/shop-cart-preview.service';
import { type AddressBookTag, injectAddressBooksTagsQuery } from '@integrations/address-books-tags';

enum CurrentSettings {
    MAIN = 'main',
    BUTTON = 'button',
    ELEMENT = 'element',
    INPUT = 'input',
    ADDITIONAL = 'additional',
    ANALYTICS = 'analytics',
    CART_STYLE = 'cartStyle',
    CART_ICON_STYLE = 'cartIconStyle',
    PAYMENT = 'payment',
    MINI_CART = 'miniCart',
    MINI_CART_BUTTON = 'miniCartButton',
}

@Component({
    selector: 'web-form-control',
    templateUrl: './shop-cart-control.component.html',
    styleUrls: ['./shop-cart-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        WebSettingsModule,
        WebPaymentMethodsCollectionComponent,
        FormsModule,
        NgxIntlTelInputModule,
        ReactiveFormsModule,
        WebCrmPipelinesCollectionComponent,
        SpSwitchComponent,
        AddressBooksUiComponent,
        CdkDropList,
        CdkDrag,
        CdkDropListGroup,
        CdkDragHandle,
        SpControlHandleComponent,
        SpVariablesPopoverComponent,
        ShopCartFormButtonSettingsComponent,
        ShopCartFormElementSettingsComponent,
        ShopCartFormInputSettingsComponent,
        ShopCartFormAdditionalSettingsComponent,
        ShopCartFormPaymentMethodSettingsComponent,
        ShopCartStyleSettingsComponent,
        ShopCartIconSettingsComponent,
        ShopCartMiniCartSettingsComponent,
        WebAnalyticsSettingsModule,
        TranslateModule,
        HtmlToTextPipe,
        WebCustomIdComponent,
        WebTagsCollectionComponent,
        TooltipModule,
    ],
})
export class ShopCartControlComponent extends WebBaseControlComponent<ShopCartWidgetData> {
    private readonly isAdmin = toSignal(this.appFacade.isAdmin$, { initialValue: false });
    private readonly siteFacade: SiteFacade = inject(SiteFacade);
    private readonly shopCartIconHttpService: ShopCartIconHttpService = inject(ShopCartIconHttpService);
    private readonly shopCartPreviewService: ShopCartPreviewService = inject(ShopCartPreviewService);

    public miniCartEnabled = false;

    protected readonly paymentMethodsQuery = injectPaymentMethodsQuery();
    protected readonly paymentMethodsOptionsQuery = injectPaymentMethodsOptionsQuery();

    protected readonly crmTagsQuery = injectCrmTagsQuery(() => ({ enabled: !this.isAdmin() }));
    public readonly crmTags = computed<CrmTag[]>(() => (this.isAdmin() ? [] : (this.crmTagsQuery.data() ?? [])));
    public addressBookVariablesById(bookId: number): AddressBookVariable[] {
        return this.addressBooksQuery.data()?.find((b) => b.id === bookId)?.variables ?? [];
    }
    protected readonly addressBooksTagsQuery = injectAddressBooksTagsQuery(() => ({ enabled: !this.isAdmin() }));
    public readonly addressBooksTags = computed<AddressBookTag[]>(() =>
        this.isAdmin() ? [] : (this.addressBooksTagsQuery.data() ?? [])
    );

    public readonly paymentMethodsOptions = computed(() => this.paymentMethodsOptionsQuery.data() ?? []);

    protected readonly crmPipelinesQuery = injectCrmPipelinesQuery(() => ({ enabled: !this.isAdmin() }));
    public readonly crmPipelines = computed<CrmPipeline[]>(() =>
        this.isAdmin() ? [] : (this.crmPipelinesQuery.data() ?? [])
    );

    protected readonly crmContactsQuery = injectCrmContactsQuery(() => ({ enabled: !this.isAdmin() }));
    public readonly crmContacts = computed<CrmContact[]>(() =>
        this.isAdmin() ? [] : (this.crmContactsQuery.data() ?? [])
    );
    /**
     * Only a definitive rejection of contacts/attributes means CRM is disabled for the
     * account — a transient network failure leaves the CRM settings in place (#71534).
     */
    public readonly isCrmDisabled = computed(() => isCrmDisabledError(this.crmContactsQuery.error()));
    public readonly paymentMethods = computed(() => (this.isAdmin() ? [] : (this.paymentMethodsQuery.data() ?? [])));

    private readonly paymentElementTypeValue = toSignal(
        this.controlForm.valueChanges.pipe(
            startWith(this.controlForm.value),
            map((value) => value?.data?.paymentElements?.[0]?.type ?? null)
        )
    );

    private readonly isUserTariffFree = toSignal(this.userFacade.isUserTariffFree$, { initialValue: false });

    public get showNonCustomPaymentRestriction(): boolean {
        return (
            this.isUserTariffFree() &&
            this.paymentElements.value.some((paymentElement) => paymentElement?.type !== EPaymentType.custom)
        );
    }

    public readonly isHasSuccessPage = computed(
        () =>
            this.paymentMethodsOptions().find(
                (item: PaymentMethodOption) => item.paymentMethodType === this.paymentElementTypeValue()
            )?.isBackUrlSuccess ?? false
    );

    /**
     * The deals query key follows the selected pipeline. `selectedPipeline` is
     * patched with `emitEvent: false`, so the signal is fed from `setPipeline`
     * directly and from the store-driven `widget$` stream (init, undo/redo).
     */
    private readonly selectedPipelineId = signal<number | null>(null);
    protected readonly crmDealsQuery = injectCrmDealsQuery(
        () => this.selectedPipelineId(),
        () => ({ enabled: !this.isAdmin() })
    );
    public readonly crmDeals = computed<CrmDeal[]>(() => (this.isAdmin() ? [] : (this.crmDealsQuery.data() ?? [])));

    protected readonly addressBooksQuery = injectAddressBooksQuery();

    public readonly radioGroupType = ERadioGroupType;
    public readonly cartViewStyleType = ECartViewType;
    public readonly FormStyleEnum = FormStyleEnum;
    protected readonly pixelTypes = EPixelTypes;
    protected readonly EButtonType = EButtonType;

    public currentSettings: CurrentSettings = CurrentSettings.MAIN;
    public settingEnum = CurrentSettings;
    public editedInputIndex: number = null;
    public editingPaymentIndex: number = null;

    public readonly CREATE_DEAL_AND_CONTACT = [
        { label: this.translateService.instant('create_deal_and_contact'), value: true },
        { label: this.translateService.instant('create_only_contact'), value: false },
    ];

    protected initForm(): void {
        this.controlForm = this.fb.group({
            data: shopCartWidgetDataFormGroup(),
        });
    }

    protected setFormData(): void {
        if (this.widgetData.paymentElements.length !== this.paymentElements.value.length) {
            this.paymentElements.clear({ emitEvent: false });
            this.widgetData.paymentElements.forEach(() => {
                this.paymentElements.push(paymentElementFormGroup(), { emitEvent: false });
            });
        }

        if (this.widgetData.elements.length !== this.elements.value.length) {
            this.elements.clear({ emitEvent: false });
            this.widgetData.elements.forEach((input: FormElement) => {
                const form = formElementForm(input.type);
                if (input.type === 'radio' || input.type === 'select') {
                    input.options.forEach(() => {
                        (form.get('options') as OptionFormArray).push(formElementOptionFormGroup());
                    });
                }

                this.elements.push(form, { emitEvent: false });
            });
            this.widgetData.items.forEach(() => {
                const form = new FormGroup({
                    name: new FormControl(null),
                    description: new FormControl(null),
                    shortDescription: new FormControl(null),
                    price: new FormControl(null),
                    oldPrice: new FormControl(null),
                    images: new FormControl(null),
                    sections: new FormControl(null),
                    vendorCode: new FormControl(null),
                    quantity: new FormControl(null),
                    unit: new FormControl(null),
                    id: new FormControl(null),
                });

                this.items.push(form, { emitEvent: false });
            });
        }

        if (this.widgetData.contactTags.length !== this.contactTags.value.length) {
            this.contactTags.clear({ emitEvent: false });
            this.widgetData.contactTags.forEach((tagId: number) => {
                this.contactTags.push(this.fb.control(tagId), { emitEvent: false });
            });
        }

        if (this.widgetData.contactAddressBookTags.length !== this.contactAddressBookTags.value.length) {
            this.contactAddressBookTags.clear({ emitEvent: false });
            this.widgetData.contactAddressBookTags.forEach((tagId: number) => {
                this.contactAddressBookTags.push(this.fb.control(tagId), { emitEvent: false });
            });
        }

        this.controlForm.patchValue({ data: this.widgetData }, { emitEvent: false });
    }

    protected initSubscriptions(): void {
        this.selectedAddressBook.valueChanges
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(() => this.clearAddressBookVariables());

        // Keep the deals-query pipeline id in sync with the store (initial
        // load, undo/redo) — the form control is patched without emitting.
        this.widget$
            .pipe(
                filter((widget) => Boolean(widget)),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((widget) => {
                this.selectedPipelineId.set((widget.data as ShopCartWidgetData)?.selectedPipeline?.id ?? null);
            });

        this.miniCartSettings.valueChanges
            .pipe(debounceTime(500), takeUntil(this.unsubscribe$))
            .subscribe(() => this.saveMiniCartSettings());

        this.loadMiniCartSettings();
    }

    protected beforeDestroy(): void {
        this.shopCartPreviewService.hideMiniCartPreview();
    }

    private loadMiniCartSettings(): void {
        this.siteFacade.id$.pipe(take(1)).subscribe((siteId) => {
            if (!siteId) return;
            this.shopCartIconHttpService
                .getMiniCartSettings(siteId.toString())
                .pipe(take(1))
                .subscribe((settings) => {
                    this.miniCartEnabled = settings.miniCartEnabled;
                    this.changeDetectorRef.markForCheck();
                });
        });
    }

    public toggleMiniCartEnabled(enabled: boolean): void {
        this.miniCartEnabled = enabled;
        this.saveMiniCartSettings();
    }

    private saveMiniCartSettings(): void {
        this.siteFacade.id$.pipe(take(1)).subscribe((siteId) => {
            if (!siteId) return;
            this.shopCartIconHttpService
                .setMiniCartSettings(
                    siteId.toString(),
                    this.miniCartEnabled,
                    this.miniCartSettings.getRawValue() as MiniCartSettings
                )
                .pipe(take(1))
                .subscribe();
        });
    }

    protected getWidgetValidationErrors(): WidgetErrors {
        const errors: WidgetErrors = [];

        if (this.controlForm.valid) {
            return errors;
        }

        const paymentElementForm = this.paymentElements.at(0);
        if (this.paymentFormName.hasError('required') || paymentElementForm?.invalid) {
            errors.push({
                key: this.translateService.instant('payment_form_invalid'),
            });
        }

        if (this.createAddressBook.value && this.selectedAddressBook.hasError('required')) {
            errors.push({
                key: this.translateService.instant('address_book_required'),
            });
        }

        if (this.selectedPipeline.hasError('required')) {
            errors.push({
                key: this.translateService.instant('pipeline_required'),
            });
        }

        if (this.dealName.hasError('required')) {
            errors.push({
                key: this.translateService.instant('deal_name_required'),
            });
        }

        this.elements.controls.forEach((control: FormElementFormGroup) => {
            if (
                control.get('type').value === EFormInputType.email ||
                control.get('type').value === EFormInputType.phone
            ) {
                return;
            }

            if (control.hasError('atLeastOne', 'crmContactVariable')) {
                errors.push({
                    key: this.translateService.instant('variable_required'),
                });
            }
        });

        return errors;
    }

    public setSelectedAddressBook(val: number, emitEvent: boolean = true): void {
        this.selectedAddressBook.patchValue(val, { emitEvent });
    }

    public setIsAddressBook(val: boolean): void {
        this.setSelectedAddressBook(null, false);
        this.createAddressBook.patchValue(val);
    }

    public toggleAddressBookSettings(val: boolean): void {
        if (!val) {
            this.clearAddressBookVariables();
        }

        this.setIsAddressBook(val);
    }

    public setPipeline(pipeline: CrmPipeline, step: CrmPipelineStep, settings: CrmPipelineSetting[]): void {
        this.selectedPipeline.patchValue(pipeline, { emitEvent: false });
        this.selectedPipelineStep.patchValue(step, { emitEvent: false });
        this.selectedPipelineSettings.patchValue(settings, { emitEvent: false });
        this.selectedPipelineId.set(pipeline?.id ?? null);
        this.clearCrmVariables();
    }

    public setInputSettingsHorizontalPosition(val: string): void {
        this.inputSettingsHorizontalPosition.patchValue(val as EHorizontalPosition);
    }

    private clearCrmVariables(): void {
        const elements = this.elements.getRawValue().map((el) => ({
            ...el,
            crmContactVariable: null,
            crmDealVariable: null,
        }));

        this.elements.patchValue(elements);
    }

    private clearAddressBookVariables(emitEvent: boolean = false): void {
        const elements = this.elements.getRawValue().map((el) => ({
            ...el,
            variable: el.type === EFormInputType.email || el.type === EFormInputType.phone ? el.variable : null,
        }));

        this.elements.patchValue(elements, { emitEvent });
    }

    public setCurrentSettingsType(val: CurrentSettings): void {
        this.currentSettings = val;

        if (val === CurrentSettings.MINI_CART) {
            this.shopCartPreviewService.showMiniCartPreview();
        } else {
            this.shopCartPreviewService.hideMiniCartPreview();
        }
    }

    public editInput(i: number): void {
        this.editedInputIndex = i;

        this.currentSettings = this.settingEnum.ELEMENT;
    }

    public removeInput(i: number): void {
        this.elements.removeAt(i);
    }

    public addInput(): void {
        const form = formElementForm(EFormInputType.input);
        form.patchValue({
            type: EFormInputType.input,
            label: this.translateService.instant('control_entry_field'),
            variable: null,
            crmContactVariable: null,
            crmDealVariable: null,
            placeholder: this.translateService.instant('control_input_placeholder'),
            required: false,
            protected: false,
            isDynamicPlaceholderEnabled: false,
            htmlInputType: EHtmlInputType.text,
            autocompleteType: EHtmlAutocompleteAttr.off,
        });

        this.elements.push(form);
    }

    public getControlHandleIcon(type: string): string {
        return `./assets/img/websites/forms-icons/lpc-f-${type}-icon.svg`;
    }

    public dropElementItem(event: CdkDragDrop<ShopCartWidgetDataFormType['elements']>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        moveItemInArray(this.elements.controls, event.previousIndex, event.currentIndex);
        this.elements.patchValue(this.elements.controls.map((control) => control.getRawValue()));
    }

    public trackByElementIndex(index: number): number {
        return index;
    }

    public toggleAddCrmTags(val: boolean): void {
        if (!val) {
            this.contactTags.clear({ emitEvent: false });
        }

        this.addCrmTags.patchValue(val);
    }

    public onContactTagsChange(tagIds: number[]): void {
        this.contactTags.clear();
        tagIds.forEach((tagId) => this.contactTags.push(new FormControl(tagId)));
    }

    public toggleAddAddressBookTags(val: boolean): void {
        if (!val) {
            this.contactAddressBookTags.clear({ emitEvent: false });
        }

        this.addAddressBookTags.patchValue(val);
    }

    public onContactAddressBookTagsChange(tagIds: number[]): void {
        this.contactAddressBookTags.clear();
        tagIds.forEach((tagId) => this.contactAddressBookTags.push(new FormControl(tagId)));
    }

    public get inputSettings(): ShopCartWidgetDataFormType['inputSettings'] {
        return this.data.get('inputSettings') as ShopCartWidgetDataFormType['inputSettings'];
    }

    public get formStyleType() {
        return this.inputSettings.get('formStyleType');
    }

    public get inputSettingsHorizontalPosition(): FormInputSettingsFormType['horizontalPosition'] {
        return this.inputSettings.get('horizontalPosition') as FormInputSettingsFormType['horizontalPosition'];
    }

    public get elements(): ShopCartWidgetDataFormType['elements'] {
        return this.data.get('elements') as ShopCartWidgetDataFormType['elements'];
    }

    public get items(): ShopCartWidgetDataFormType['items'] {
        return this.data.get('items') as ShopCartWidgetDataFormType['items'];
    }

    public get dealName(): ShopCartWidgetDataFormType['dealName'] {
        return this.data.get('dealName') as ShopCartWidgetDataFormType['dealName'];
    }

    public get selectedAddressBook(): ShopCartWidgetDataFormType['selectedAddressBook'] {
        return this.data.get('selectedAddressBook') as ShopCartWidgetDataFormType['selectedAddressBook'];
    }

    public get selectedPipeline(): ShopCartWidgetDataFormType['selectedPipeline'] {
        return this.data.get('selectedPipeline') as ShopCartWidgetDataFormType['selectedPipeline'];
    }

    public get selectedPipelineStep(): ShopCartWidgetDataFormType['selectedPipelineStep'] {
        return this.data.get('selectedPipelineStep') as ShopCartWidgetDataFormType['selectedPipelineStep'];
    }

    public get selectedPipelineSettings(): ShopCartWidgetDataFormType['selectedPipelineSettings'] {
        return this.data.get('selectedPipelineSettings') as ShopCartWidgetDataFormType['selectedPipelineSettings'];
    }

    public get createAddressBook(): ShopCartWidgetDataFormType['createAddressBook'] {
        return this.data.get('createAddressBook') as ShopCartWidgetDataFormType['createAddressBook'];
    }

    public get formButtonSettings(): ShopCartWidgetDataFormType['formButtonSettings'] {
        return this.data.get('formButtonSettings') as ShopCartWidgetDataFormType['formButtonSettings'];
    }

    public get label(): FormButtonSettingsFormType['label'] {
        return this.formButtonSettings.get('label') as FormButtonSettingsFormType['label'];
    }

    public get additionalFormSettings(): ShopCartWidgetDataFormType['additionalFormSettings'] {
        return this.data.get('additionalFormSettings') as ShopCartWidgetDataFormType['additionalFormSettings'];
    }

    public get cartSettings(): ShopCartWidgetDataFormType['cartSettings'] {
        return this.data.get('cartSettings') as ShopCartWidgetDataFormType['cartSettings'];
    }

    public get cartIconSettings(): ShopCartWidgetDataFormType['cartIconSettings'] {
        return this.data.get('cartIconSettings') as ShopCartWidgetDataFormType['cartIconSettings'];
    }

    public get miniCartSettings(): ShopCartWidgetDataFormType['miniCartSettings'] {
        return this.data.get('miniCartSettings') as ShopCartWidgetDataFormType['miniCartSettings'];
    }

    public get createContactAndDeal(): ShopCartWidgetDataFormType['createContactAndDeal'] {
        return this.data.get('createContactAndDeal') as ShopCartWidgetDataFormType['createContactAndDeal'];
    }

    public get cartViewType(): ShopCartWidgetDataFormType['cartViewType'] {
        return this.data.get('cartViewType') as ShopCartWidgetDataFormType['cartViewType'];
    }

    public get paymentElements(): ShopCartWidgetDataFormType['paymentElements'] {
        return this.data.get('paymentElements') as ShopCartWidgetDataFormType['paymentElements'];
    }

    public get paymentElement(): PaymentElementFormGroup {
        return this.paymentElements.at(0);
    }

    public get paymentElementType(): PaymentElementFormType['type'] {
        return this.paymentElement.get('type') as PaymentElementFormType['type'];
    }

    public get paymentFormName(): ShopCartWidgetDataFormType['paymentFormName'] {
        return this.data.get('paymentFormName') as ShopCartWidgetDataFormType['paymentFormName'];
    }

    public get analyticsSettings(): ShopCartWidgetDataFormType['analyticsSettings'] {
        return this.data.get('analyticsSettings') as ShopCartWidgetDataFormType['analyticsSettings'];
    }

    public get addCrmTags(): ShopCartWidgetDataFormType['addCrmTags'] {
        return this.data.get('addCrmTags') as ShopCartWidgetDataFormType['addCrmTags'];
    }

    public get contactTags(): ShopCartWidgetDataFormType['contactTags'] {
        return this.data.get('contactTags') as ShopCartWidgetDataFormType['contactTags'];
    }

    public get addAddressBookTags(): ShopCartWidgetDataFormType['addAddressBookTags'] {
        return this.data.get('addAddressBookTags') as ShopCartWidgetDataFormType['addAddressBookTags'];
    }

    public get contactAddressBookTags(): ShopCartWidgetDataFormType['contactAddressBookTags'] {
        return this.data.get('contactAddressBookTags') as ShopCartWidgetDataFormType['contactAddressBookTags'];
    }

    public onSwitchCartViewType(value: ECartViewType): void {
        this.cartViewType.setValue(value);

        if (value === ECartViewType.HORIZONTAL) {
            this.inputSettings.patchValue({
                ...this.inputSettings.value,
                formStyleType: FormStyleEnum.VERTICAL,
            });
        } else {
            this.inputSettings.patchValue({
                ...this.inputSettings.value,
                formStyleType: FormStyleEnum.HORIZONTAL,
            });
        }
    }

    public editPaymentElement(index: number): void {
        this.editingPaymentIndex = index;
        this.currentSettings = this.settingEnum.PAYMENT;
    }

    public setPaymentElements(paymentElements: Partial<PaymentElement>[]): void {
        this.paymentElements.patchValue(paymentElements);
    }

    public isDisabledDelete(element): boolean {
        return element.type === EFormInputType.email;
    }

    public setPipelineName(name: string) {
        this.selectedPipeline.patchValue({ ...this.selectedPipeline.value, name }, { emitEvent: false });
    }

    public get miniCartButtonSettings(): MiniCartSettingsFormType['buttonSettings'] {
        return this.miniCartSettings.controls.buttonSettings;
    }

    public get miniCartButtonLabel(): FormControl<string> {
        return this.miniCartButtonSettings.get('label') as FormControl<string>;
    }
}
